import { useEffect, useState } from 'react';
import teacherService from 'ApiService/Admin/teacherService';
import toast from 'react-hot-toast';
import { ThreeDots } from 'react-loader-spinner';

const TeacherDetailModal = ({ isOpen, onClose, teacherId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!teacherId) return;
      setLoading(true);
      try {
        const response = await teacherService.getTeacherById(teacherId);
        if (response.data.success) {
          setData(response.data.data);
        } else {
          toast.error('Failed to fetch teacher details');
        }
      } catch (error) {
        toast.error('Error loading teacher details');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchDetails();
    }
  }, [isOpen, teacherId]);

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-[999] flex items-center justify-center bg-opacity-50 backdrop-blur-sm">
      <div className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800 md:mx-0 md:p-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between border-b pb-4 dark:border-white/10">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Teacher Profile & Assignments
          </h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
          >
            &times;
          </button>
        </div>

        {loading ? (
          <div className="flex h-60 flex-col items-center justify-center">
            <ThreeDots color="#5431ed" />
            <p className="mt-4 text-gray-500">Loading teacher data...</p>
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* Personal Information */}
            <section>
              <h3 className="mb-4 text-lg font-semibold text-brand-500">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <DetailItem label="Full Name" value={data.teacher?.name} />
                <DetailItem label="Email Address" value={data.teacher?.email} />
                <DetailItem label="Contact No" value={data.teacher?.contact} />
                <DetailItem label="Department" value={data.teacher?.department?.name} />
                <DetailItem label="Branch" value={data.teacher?.branch?.name} />
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase text-gray-400">Status</span>
                  <span className={`mt-1 inline-flex w-fit rounded-full px-3 py-1 text-xs font-bold ${data.teacher?.isBlocked ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                    {data.teacher?.isBlocked ? 'Inactive' : 'Active'}
                  </span>
                </div>
              </div>
            </section>

            <hr className="dark:border-white/10" />

            {/* Assigned Batches */}
            <section>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-brand-500">
                  Assigned Batches
                </h3>
                <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-bold text-brand-500 dark:bg-brand-400/10">
                  {data.batches?.length || 0} Total
                </span>
              </div>

              <div className="space-y-3">
                {data.batches?.length > 0 ? (
                  data.batches.map((batch) => (
                    <div
                      key={batch._id}
                      className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 transition-all hover:border-brand-200 dark:border-white/10 dark:bg-navy-700"
                    >
                      <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
                        <div>
                          <p className="font-bold text-navy-700 dark:text-white">
                            {batch.batchStructure?.courses?.map(c => c.courseName).join(', ') || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {batch.batchStructure?.name || 'N/A'}
                          </p>
                        </div>
                        <div className="flex flex-col items-start md:items-end">
                          <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-bold text-purple-600 dark:bg-purple-400/10">
                            Batch {batch.batchNumber}
                          </span>
                          <span className="mt-1 text-[10px] text-gray-400">
                            {batch.branches?.map(b => b.name).join(', ') || 'Global'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="italic text-gray-400">No batches assigned yet.</p>
                )}
              </div>
            </section>
          </div>
        ) : (
          <p className="text-center text-red-500">Teacher not found.</p>
        )}

        {/* Footer */}
        <div className="mt-8 flex justify-end border-t pt-6 dark:border-white/10">
          <button
            onClick={onClose}
            className="rounded-xl bg-brand-500 px-8 py-3 text-sm font-bold text-white transition hover:bg-brand-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs font-bold uppercase text-gray-400">{label}</span>
    <span className="mt-1 font-medium text-navy-700 dark:text-white">
      {value || 'N/A'}
    </span>
  </div>
);

export default TeacherDetailModal;
